/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { CarouselProvider } from '@/components/blocks/Carousel/CarouselProvider';
import { CarouselSlide } from '@/components/blocks/Carousel/CarouselSlide';
import { CarouselProps } from '@/components/blocks/ProductDetails/Carousel';
import { productDetailsThumbnailSliderSX } from '@/components/blocks/ProductDetails/styles/thumbnailSlider';
import { ProductImage } from '@/components/blocks/ProductImage';
import { useTheme } from '@mui/material/styles';
import {
	Box,
	ClickAwayListener,
	IconButton,
	Stack,
	Tooltip,
	tooltipClasses,
	TooltipProps,
	useMediaQuery,
} from '@mui/material';
import { DotGroup, Slider } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import React, { FC, useEffect, useState } from 'react';
import { ProductDetailsAddToWishList } from '@/components/blocks/ProductDetails/AddToWishList';
import styled from '@emotion/styled';
import { Img } from '@/components/blocks/MaterialImage';
import ShareIcon from '@mui/icons-material/Share';

const MD_UP_DIMS = { slideWidth: 12, slideHeight: 15, visibleSlides: 8 };
const SM_DIMS = { slideWidth: 5, slideHeight: 3, visibleSlides: 1 };
const XS_DIMS = { slideWidth: 1, slideHeight: 1, visibleSlides: 1 };

export const ProductDetailsThumbnailCarousel: FC<CarouselProps> = ({
	slides,
	choose,
	index,
	ribbons,
	onOpenFullImage,
	onClick,
}) => {
	const theme = useTheme();
	const isSm = useMediaQuery(theme.breakpoints.down('md'));
	const isXs = useMediaQuery(theme.breakpoints.down('sm'));
	const isThumbnail = !isSm;
	const { visibleSlides } = isXs ? XS_DIMS : isSm ? SM_DIMS : MD_UP_DIMS;
	const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
		<Tooltip {...props} classes={{ popper: className }} />
	))(() => ({
		[`& .${tooltipClasses.tooltip}`]: {
			backgroundColor: '#F1F1F1',
			color: 'rgba(0, 0, 0, 0.87)',
			fontSize: 'var(--font-size-tiny)',
		},
		[`& .${tooltipClasses.arrow}`]: {
			color: '#F1F1F1',
		},
	}));

	const [openShare, setOpenShare] = useState(false);

	const [urlCompleta, setUrlCompleta] = useState('');

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const urlActual = window.location.href;
			setUrlCompleta(urlActual);
		}
	}, []);

	const handleShare = () => {
		setOpenShare(true);
	};

	const handleTooltipClose = () => {
		setOpenShare(false);
	};

	const handleShareWhatsapp = () => {
		const message = encodeURIComponent(
			`¡Te recomiendo este producto que vi en dportenis! ${urlCompleta}`
		);
		const linkWhatsApp = `https://wa.me/?text=${message}`;
		window.open(linkWhatsApp, '_blank');
	};

	const handleShareFacebook = () => {
		window?.open(`https://www.facebook.com/sharer.php?s=100&u=${window?.location?.href}`, '_blank');
	};
	const imagesToRender = slides.map(({ thumbnail, name, fullImage }) => {
		const src = isXs ? fullImage : isSm ? fullImage : thumbnail;
		return { src, name };
	});

	return (
		<Box sx={{ position: 'relative', zIndex: 1000 }}>
			{isSm ? <ProductDetailsAddToWishList isIconFavorite inThumbnail /> : null}
			{isSm ? (
				<ClickAwayListener onClickAway={handleTooltipClose}>
					<Box>
						<LightTooltip
							open={openShare}
							sx={{
								'& .MuiTooltip-arrow': {
									backgroundColor: '#F1F1F1',
								},
							}}
							title={
								<Stack direction="row" spacing={2} padding={0.5}>
									<Img
										id="pdp-facebook-share"
										src="/images/pdp-facebook.png"
										alt="Facebook"
										sx={{ width: '27px', height: '27px', cursor: 'pointer' }}
										onClick={handleShareFacebook}
									/>
									<Img
										id="pdp-instagram-share"
										src="/images/whatsapp.png"
										alt="Instagram"
										sx={{ width: '27px', height: '27px', cursor: 'pointer' }}
										onClick={handleShareWhatsapp}
									/>
								</Stack>
							}
							arrow
						>
							<IconButton
								id="pdp-share-button"
								aria-label="share"
								sx={{
									backgroundColor: '#F1F1F1',
									position: 'absolute',
									bottom: '65px',
									right: '19px',
									zIndex: 1001,
								}}
								onClick={handleShare}
							>
								<ShareIcon />
							</IconButton>
						</LightTooltip>
					</Box>
				</ClickAwayListener>
			) : null}

			<CarouselProvider
				naturalSlideWidth={396}
				naturalSlideHeight={396}
				visibleSlides={visibleSlides}
				step={visibleSlides}
				infinite={true}
				dragEnabled={isSm}
				totalSlides={imagesToRender.length}
				orientation={isSm ? 'horizontal' : 'vertical'}
				sx={productDetailsThumbnailSliderSX(imagesToRender.length)}
			>
				<Slider>
					{imagesToRender.map(({ name = '', src }, i) => (
						<CarouselSlide
							index={i}
							key={`${isSm}-${i}`}
							onFocus={() => choose(i)}
							onClick={(event) => {
								choose(i);
								onClick?.(event);
							}}
							sx={{
								paddingBottom: {
									xs: 'none',
								},
							}}
						>
							<ProductImage
								src={src}
								alt={name}
								ribbons={isSm ? ribbons : []}
								isThumbnail={isThumbnail}
								isSelected={!isSm ? i === index : false}
								isPDP={isSm}
								indexImage={i + 1}
								totalSlides={imagesToRender.length}
								onOpenFullImage={onOpenFullImage}
							/>
						</CarouselSlide>
					))}
				</Slider>

				{isSm ? <DotGroup /> : null}
			</CarouselProvider>
		</Box>
	);
};
